/**
 * LinkedIssuesSection Component
 *
 * Displays linked issues grouped by relationship type
 * (related, duplicate, blocks, blocked_by, caused_by, causes)
 */

import React, { useState, useEffect } from 'react';
import {
  LinkIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import enhancedApiService from '../../api/enhancedApiService';
import { useUI } from '../../contexts/UIContext';
import ConfirmationModal from '../common/ConfirmationModal';

const LinkedIssuesSection = ({ issueId, onLinkDeleted, refreshTrigger }) => {
  const [links, setLinks] = useState({
    related: [],
    duplicate: [],
    blocks: [],
    blocked_by: [],
    caused_by: [],
    causes: []
  });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const { showNotification } = useUI();

  useEffect(() => {
    if (issueId && issueId !== 'new') {
      loadLinks();
    }
  }, [issueId, refreshTrigger]); // Add refreshTrigger to dependencies

  const loadLinks = async () => {
    try {
      setLoading(true);
      const response = await enhancedApiService({
        method: 'GET',
        url: `/issues/${issueId}/links`
      });

      if (response.success) {
        setLinks(response.links || {});
      }
    } catch (error) {
      console.error('Error loading issue links:', error);
      showNotification('Failed to load linked issues', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLinkClick = (link, linkType) => {
    setLinkToDelete({ link, linkType });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!linkToDelete) return;

    const { link, linkType } = linkToDelete;

    try {
      setDeleting(link.id);
      const response = await enhancedApiService({
        method: 'DELETE',
        url: `/issues/${issueId}/links/${link.id}`
      });

      if (response.success) {
        showNotification('Link removed successfully', 'success');
        // Reload links
        await loadLinks();
        if (onLinkDeleted) {
          onLinkDeleted();
        }
      }
    } catch (error) {
      console.error('Error deleting link:', error);
      showNotification('Failed to remove link', 'error');
    } finally {
      setDeleting(null);
      setLinkToDelete(null);
    }
  };

  const getLinkTypeLabel = (type) => {
    const labels = {
      related: 'Related To',
      duplicate: 'Duplicates',
      blocks: 'Blocks',
      blocked_by: 'Blocked By',
      caused_by: 'Caused By',
      causes: 'Causes'
    };
    return labels[type] || type;
  };

  const getLinkTypeColor = (type) => {
    const colors = {
      related: 'text-blue-700 bg-blue-50 border-blue-200',
      duplicate: 'text-purple-700 bg-purple-50 border-purple-200',
      blocks: 'text-red-700 bg-red-50 border-red-200',
      blocked_by: 'text-orange-700 bg-orange-50 border-orange-200',
      caused_by: 'text-yellow-700 bg-yellow-50 border-yellow-200',
      causes: 'text-pink-700 bg-pink-50 border-pink-200'
    };
    return colors[type] || 'text-gray-700 bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case 'in_progress':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'resolved':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      default:
        return null;
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[severity] || colors.medium}`}>
        {severity}
      </span>
    );
  };

  const renderLinkGroup = (type, linkList) => {
    if (!linkList || linkList.length === 0) {
      return null;
    }

    return (
      <div key={type} className="space-y-2">
        <h4 className={`text-sm font-medium px-2 py-1 rounded border ${getLinkTypeColor(type)}`}>
          {getLinkTypeLabel(type)} ({linkList.length})
        </h4>
        <div className="space-y-2">
          {linkList.map((link) => (
            <div
              key={link.id}
              className="flex items-start justify-between p-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {getStatusIcon(link.target_issue?.status)}
                  <a
                    href={`/issues/${link.target_issue?.id}`}
                    className="text-sm font-medium text-gray-900 hover:text-blue-600"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = `/issues/${link.target_issue?.id}`;
                    }}
                  >
                    {link.target_issue?.title}
                  </a>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">
                    Issue #{link.target_issue?.id?.split('-')[0]}
                  </span>
                  {getSeverityBadge(link.target_issue?.severity)}
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    link.target_issue?.status === 'resolved'
                      ? 'bg-green-100 text-green-700'
                      : link.target_issue?.status === 'in_progress'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {link.target_issue?.status || 'unknown'}
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleDeleteLinkClick(link, type)}
                disabled={deleting === link.id}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Remove link"
              >
                {deleting === link.id ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                ) : (
                  <XMarkIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">Loading linked issues...</span>
        </div>
      </div>
    );
  }

  const totalLinks = Object.values(links).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  if (totalLinks === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <LinkIcon className="h-5 w-5" />
          Linked Issues
        </h3>
        <div className="text-center py-8 text-gray-500">
          <LinkIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No linked issues yet</p>
          <p className="text-xs mt-1">Link related issues to track dependencies and relationships</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <LinkIcon className="h-5 w-5" />
          Linked Issues ({totalLinks})
        </h3>

        <div className="space-y-4">
          {Object.entries(links).map(([type, linkList]) => renderLinkGroup(type, linkList))}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setLinkToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Remove Link"
        message={`Are you sure you want to remove this link? ${
          linkToDelete?.link?.target_issue?.title
            ? `This will unlink "${linkToDelete.link.target_issue.title}".`
            : ''
        }`}
        confirmLabel="Remove Link"
        cancelLabel="Cancel"
        variant="danger"
      />
    </>
  );
};

export default LinkedIssuesSection;
